/**
 * Home layout X, "Just type". Prompt-first: the hero is a big ask box that types an example by
 * itself, then shows the AI's steps, the clips it picked and a tall preview playing the cut. The
 * store badges sit right under the headline. Then: use the AI you already have (the Claude Code
 * critter turning into the cherry), the phone (Isaac's spot in the middle phone), pick by hand
 * (the real sheet + uploader), the seven-way hub, pricing. Light cream, calm.
 */
import React from "react";
import { HOME3_CSS } from "./home3Css";
import { AiCutDemo, BrandIcon, ClipSheet, LoopVideo, PhoneShot, PhoneVideo, PlanCards, StoreBadges, UploadCard, scrollToId, useHomeFonts } from "./kit";
import { GetAppBar, H3Footer, H3Nav, useHomeActions } from "./chrome";
import { BADGES, LANGS, NAV, PLANS, X } from "../../constants/homeLayoutsText";

const LayoutXPage: React.FC = () => {
  useHomeFonts();
  const act = useHomeActions();
  return (
    <div className="h3 h3-x">
      <style>{HOME3_CSS + X_CSS}</style>
      <H3Nav
        onLight
        links={[
          { id: "x-try", label: NAV.ai },
          { id: "x-phone", label: NAV.phone },
          { id: "x-hand", label: NAV.web },
          { id: "x-hub", label: NAV.everywhere },
          { id: "x-pricing", label: NAV.pricing },
        ]}
      />

      <section className="x-hero" id="x-try">
        <div className="wrap">
          <div className="x-hero-head">
            <span className="eyebrow">{X.eyebrow}</span>
            <h1>
              {X.title1}
              <br />
              <span className="rw">{X.title2}</span>
            </h1>
            <p className="lede">{X.sub}</p>
            <div className="x-ctas">
              <button type="button" className="h3-btn cherry" onClick={() => scrollToId("h3-upload")}>
                {X.upload}
              </button>
              <span className="x-or">{X.orApp}</span>
              <StoreBadges size="md" />
            </div>
          </div>
          <AiCutDemo layout="hero" cycleExamples />
        </div>
      </section>

      <section id="x-ai" className="x-ai">
        <div className="wrap x-ai-grid">
          <LoopVideo className="h3-loop" src="/home/anim-claude-cherry.mp4" poster="/home/anim-claude-cherry.jpg" label="Claude's critter turns into the CherryClipz cherry after picking 9 clips" />
          <div>
            <span className="eyebrow">{X.aiEyebrow}</span>
            <h2 className="h2">{X.aiTitle}</h2>
            <ol className="x-steps">
              {X.aiSteps.map((s, k) => (
                <li key={s.t}>
                  <span className="n">{k + 1}</span>
                  <div>
                    <b>{s.t}</b>
                    <p>{s.b}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="x-with">
              <BrandIcon k="claude" size={40} />
              <BrandIcon k="openai" size={40} />
            </div>
          </div>
        </div>
      </section>

      <section id="x-phone" className="x-phone">
        <div className="wrap">
          <div className="x-center">
            <span className="eyebrow">{X.phoneEyebrow}</span>
            <h2 className="h2">{X.phoneTitle}</h2>
            <p className="lede">{X.phoneBody}</p>
            <div id="h3-get">
              <StoreBadges qr />
            </div>
          </div>
          <div className="x-phones">
            <PhoneShot src="/home/phone-4.jpg" alt="Clips picked on the phone, in order, with a timeline at the bottom" className="side" />
            <PhoneVideo src="/home/stop-cutting.mp4" poster="/home/stop-cutting.jpg" label="Stop cutting your videos: a 24-second CherryClipz spot" />
            <PhoneShot src="/home/phone-2.jpg" alt="Make the video: tall shape with captions" className="side" />
          </div>
        </div>
      </section>

      <section id="x-hand" className="x-hand">
        <div className="wrap">
          <div className="two">
            <div>
              <span className="eyebrow">{X.handEyebrow}</span>
              <h2 className="h2">{X.handTitle}</h2>
            </div>
            <p className="lede">{X.handBody}</p>
          </div>
          <div style={{ marginTop: "clamp(36px,4vw,56px)" }}>
            <ClipSheet autoDemo={false} height={420} />
          </div>
          <div className="x-upload">
            <UploadCard />
            <p className="langs">{LANGS}</p>
          </div>
        </div>
      </section>

      <section id="x-hub" className="x-hub">
        <div className="wrap x-hub-grid">
          <div>
            <span className="eyebrow">{X.hubEyebrow}</span>
            <h2 className="h2">{X.hubTitle}</h2>
            <p className="lede" style={{ marginTop: 18 }}>
              {X.hubBody}
            </p>
          </div>
          <LoopVideo className="h3-loop" src="/home/anim-hub7.mp4" poster="/home/anim-hub7.jpg" label="CherryClipz in the middle, connected to Claude, ChatGPT, Premiere Pro, the App Store, Google Play, YouTube and make.com" />
        </div>
      </section>

      <section id="x-pricing">
        <div className="wrap">
          <div className="price-head">
            <div>
              <span className="eyebrow">{PLANS.eyebrow}</span>
              <h2 className="h2">{PLANS.title}</h2>
            </div>
            <button type="button" className="link" onClick={act.pricing}>
              {PLANS.compare}
            </button>
          </div>
          <PlanCards plans={PLANS.items} perMonth={PLANS.perMonth} onPrimary={() => scrollToId("h3-upload")} onPlans={act.pricing} />
        </div>
      </section>

      <section className="x-close">
        <div className="wrap">
          <h2>{X.closeTitle}</h2>
          <div className="x-ctas">
            <button type="button" className="h3-btn cherry" onClick={() => scrollToId("x-try")}>
              {NAV.ai}
            </button>
            <StoreBadges size="md" />
          </div>
        </div>
      </section>

      <H3Footer onLight />
      <GetAppBar text={BADGES.free} tag="x" />
    </div>
  );
};

const X_CSS = `
.h3-x .x-hero{padding-block:clamp(40px,6vw,80px) clamp(64px,8vw,100px)}
.h3-x .x-hero-head{display:grid;justify-items:center;text-align:center;gap:18px;margin-bottom:clamp(30px,4vw,48px)}
.h3-x h1{font-weight:800;font-size:clamp(46px,7.4vw,100px);line-height:.92;letter-spacing:-.05em}
.h3-x .x-hero-head .lede{max-width:60ch}
.h3-x .x-ctas{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:12px 18px;margin-top:8px}
.h3-x .x-or{font:500 13px/1 var(--fm);color:var(--ink3);text-transform:uppercase;letter-spacing:.1em}

.h3-x .x-ai{background:var(--bg2);border-block:1px solid var(--line)}
.h3-x .x-ai-grid{display:grid;grid-template-columns:minmax(0,1.1fr) minmax(0,.9fr);gap:clamp(30px,5vw,80px);align-items:center}
.h3-x .x-steps{list-style:none;margin:26px 0 0;padding:0;display:grid;gap:0;border-top:1px solid var(--line2)}
.h3-x .x-steps li{display:grid;grid-template-columns:40px 1fr;gap:14px;padding:16px 0;border-bottom:1px solid var(--line)}
.h3-x .x-steps .n{width:32px;height:32px;border-radius:50%;border:1.5px solid var(--line2);display:grid;place-items:center;font:600 13px/1 var(--fm);color:var(--cherry-t)}
.h3-x .x-steps b{font:700 18px/1.2 var(--fd);letter-spacing:-.02em}
.h3-x .x-steps p{color:var(--ink2);font-size:15.5px;margin-top:4px}
.h3-x .x-with{display:flex;gap:8px;margin-top:22px}
@media (max-width:900px){.h3-x .x-ai-grid{grid-template-columns:1fr}}

.h3-x .x-center{display:grid;justify-items:center;text-align:center;gap:18px}
.h3-x .x-center .h3-stores{justify-content:center;margin-top:8px}
.h3-x .x-phones{display:flex;justify-content:center;align-items:center;gap:clamp(14px,3vw,40px);margin-top:clamp(40px,5vw,64px)}
.h3-x .x-phones .side{width:min(24vw,250px);opacity:.95}
.h3-x .x-phones .h3-phonewrap .h3-device{width:min(30vw,320px)}
@media (max-width:700px){.h3-x .x-phones .side{display:none}.h3-x .x-phones .h3-phonewrap .h3-device{width:min(76vw,300px)}}

.h3-x .x-hand{background:var(--bg2);border-block:1px solid var(--line)}
.h3-x .x-upload{max-width:820px;margin:clamp(36px,4vw,56px) auto 0;display:grid;gap:14px}
.h3-x .x-upload .langs{text-align:center;font:500 13px/1.5 var(--fm);color:var(--ink3)}

.h3-x .x-hub-grid{display:grid;grid-template-columns:minmax(0,.8fr) minmax(0,1.2fr);gap:clamp(30px,5vw,80px);align-items:center}
@media (max-width:900px){.h3-x .x-hub-grid{grid-template-columns:1fr}}

.h3-x .x-close{text-align:center;border-top:1px solid var(--line)}
.h3-x .x-close .wrap{display:grid;justify-items:center;gap:26px}
.h3-x .x-close h2{font-weight:800;font-size:clamp(42px,6.4vw,88px);line-height:.95;letter-spacing:-.045em}
`;

export default LayoutXPage;
